import { useEffect, useRef } from 'react';
import { toast, type Id } from 'react-toastify';
// NOTE: Do not use i18n here; caller passes plain labels

type StartOptions = {
  total?: number;
  label: string;
  delayMs?: number;
  autoCloseMs?: number;
  persistent?: boolean; // if true, do not auto-dismiss on component unmount
};

/**
 * Lightweight progress toast controller.
 * - Shows after a delay (defaults to 1s (1000ms)) to avoid flicker for fast operations
 * - Updates progress bar and inline percent text
 * - Safe to call from async flows and background tasks; does not rely on component state
 */
export const useProgressToast = () => {
  const toastIdRef = useRef<Id | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const labelRef = useRef<string>('');
  const totalRef = useRef<number>(0);
  const doneRef = useRef<number>(0);
  const visibleRef = useRef<boolean>(false);
  const lastUpdateTsRef = useRef<number>(0);
  const persistentRef = useRef<boolean>(false);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Cleanup on unmount: stop pending timers and dismiss any visible toast to prevent leaks
  useEffect(() => {
    return () => {
      clearTimer();
      if (
        visibleRef.current &&
        toastIdRef.current != null &&
        persistentRef.current !== true
      ) {
        toast.dismiss(toastIdRef.current);
      }
    };
  }, []);

  const percent = () => {
    if (totalRef.current <= 0) return 0;
    return Math.min(1, Math.max(0, doneRef.current / totalRef.current));
  };

  const updateRender = () => {
    if (!visibleRef.current || toastIdRef.current == null) return;
    const p = percent();
    const pct = Math.floor(p * 100);
    const label = labelRef.current;
    toast.update(toastIdRef.current, {
      render: `${label} ${pct}%`,
      progress: p,
      isLoading: true,
      autoClose: false,
      closeOnClick: false,
    });
    lastUpdateTsRef.current = Date.now();
  };

  const show = () => {
    if (visibleRef.current) return;
    visibleRef.current = true;
    const label = labelRef.current;
    toastIdRef.current = toast.loading(`${label} 0%`, {
      closeOnClick: false,
      autoClose: false,
      progress: 0,
    });
  };

  const start = (opts: StartOptions) => {
    const { total = 0, label, delayMs = 1000, persistent = false } = opts;
    labelRef.current = label;
    totalRef.current = Math.max(0, total);
    doneRef.current = 0;
    persistentRef.current = !!persistent;
    clearTimer();
    timerRef.current = setTimeout(() => {
      show();
      updateRender();
    }, delayMs);
  };

  const setTotal = (total: number) => {
    totalRef.current = Math.max(0, total);
    // If work is declared before delayed show triggers, force-show now
    if (
      !visibleRef.current &&
      (timerRef.current != null || doneRef.current > 0)
    ) {
      clearTimer();
      show();
    }
    if (visibleRef.current) updateRender();
  };

  const addToTotal = (delta: number) =>
    setTotal(totalRef.current + Math.max(0, delta));

  const increment = (delta = 1) => {
    doneRef.current += Math.max(0, delta);
    // If progress begins before delayed show triggers, force-show now
    if (!visibleRef.current && timerRef.current != null) {
      clearTimer();
      show();
    }
    if (!visibleRef.current) return;
    const now = Date.now();
    if (now - lastUpdateTsRef.current >= 100) updateRender();
  };

  const done = (successMessage = 'All done', autoCloseMs = 2500) => {
    clearTimer();
    // If no loading toast is visible (finished too fast), show a success toast anyway
    if (!visibleRef.current || toastIdRef.current == null) {
      toast.success(successMessage, {
        autoClose: autoCloseMs,
        closeOnClick: true,
      });
    } else {
      toast.update(toastIdRef.current, {
        render: successMessage,
        isLoading: false,
        progress: 1,
        type: 'success',
        autoClose: autoCloseMs,
        closeOnClick: true,
      });
    }
    // Reset internal state for reuse
    visibleRef.current = false;
    toastIdRef.current = null;
    lastUpdateTsRef.current = 0;
    totalRef.current = 0;
    doneRef.current = 0;
    persistentRef.current = false;
  };

  const error = (errorMessage = 'Some items failed', autoCloseMs = 3000) => {
    clearTimer();
    // If no loading toast is visible, show a standalone error toast
    if (!visibleRef.current || toastIdRef.current == null) {
      toast.error(errorMessage, { autoClose: autoCloseMs, closeOnClick: true });
    } else {
      toast.update(toastIdRef.current, {
        render: errorMessage,
        isLoading: false,
        type: 'error',
        autoClose: autoCloseMs,
        closeOnClick: true,
      });
    }
    visibleRef.current = false;
    toastIdRef.current = null;
    lastUpdateTsRef.current = 0;
    totalRef.current = 0;
    doneRef.current = 0;
    persistentRef.current = false;
  };

  const cancel = () => {
    clearTimer();
    if (visibleRef.current && toastIdRef.current != null) {
      toast.dismiss(toastIdRef.current);
    }
    visibleRef.current = false;
    toastIdRef.current = null;
    lastUpdateTsRef.current = 0;
    totalRef.current = 0;
    doneRef.current = 0;
    persistentRef.current = false;
  };

  return { start, setTotal, addToTotal, increment, done, error, cancel };
};
